import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiReports } from "../api/reports";
import { toast } from "react-toastify";
import { apiWorkAreas } from "../api/workareas";
import { loadGoogle } from "./InspectorRouteMap";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

// Out of Area tab: inspections the out-of-area classifier flagged (drive time
// from the nearest anchored work area over 120 minutes). Markers use the same
// rectangular inspection-map-tag style as OpenMap (same mapId so the advanced
// markers work); the table matches the dashboard tabs.

// Threshold-aware severity; green (< threshold) only ever appears in
// spot-check mode — anchor-mode items are all over the threshold by definition.
const SEVERITY = (m, t) => m < 0 ? "ooa-dark" : m < t ? "ooa-green" : m <= t + 60 ? "ooa-amber" : m <= t + 180 ? "ooa-red" : "ooa-dark";

// Age bands (normal view): 7-day escalation, green at 0-7d. Spot-check mode
// stays time-graded (its signal is drive time from the picked spot).
const AGE_BAND = (d) => d <= 7 ? "age-green" : d <= 14 ? "age-lightgreen" : d <= 21 ? "age-amber" : d <= 30 ? "age-orange" : d <= 45 ? "age-red" : "age-darkred";

const minutesLabel = (m) => {
  if (m < 0) { return "Unreachable"; }
  return `${Math.round(m)} min`;
};

// hours:minutes (3:07) for popups; stays plain minutes under an hour.
const hoursMinutes = (m) => {
  if (m < 0) { return "Unreachable"; }
  m = Math.round(m);
  if (m < 60) { return `${m} min`; }
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
};

// Local calendar date as YYYY-MM-DD (input[type=date] format).
const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Area initials for the anchor tags: "Grand Strand" -> GS.
const areaInitials = (name) => (name || "")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .trim().split(/\s+/).map(w => w[0] || "").join("").toUpperCase();


export default function OutOfAreaTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { items, workAreas }
  // Out-of-area threshold (minutes), slider-driven: 2..4 hours in 30-min stages.
  // sliderValue tracks the thumb while dragging; thresholdMinutes (which drives
  // reclassification) only commits on release so a drag doesn't fire a fetch
  // per step and fight the thumb for position.
  const [thresholdMinutes, setThresholdMinutes] = useState(120);
  const [sliderValue, setSliderValue] = useState(120);
  // Spot check what-if: times re-measured from a clicked map point.
  const [spotMode, setSpotMode] = useState(false);
  const [spotPoint, setSpotPoint] = useState(null); // { lat, lng }
  const [spotItems, setSpotItems] = useState(null);
  // Batches: groups of flagged inspections returned to Orchid (page-local).
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(new Set()); // selected policy numbers
  const selectedRef = useRef(selected); // click closures read this (no marker rebuilds on selection)
  const [hiddenBatches, setHiddenBatches] = useState(new Set()); // batch ids off the map
  const [editingReturn, setEditingReturn] = useState(null); // batch id whose return date is being set
  const [expandedBatch, setExpandedBatch] = useState(null); // batch id whose member table is open
  const [returnDateDraft, setReturnDateDraft] = useState("");

  const loadBatches = useCallback(() => {
    apiReports.getOutOfAreaBatches().then((res) => {
      if (res?.data?.status === 200) { setBatches(res.data.result || []); }
    }).catch(() => {});
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const mapDivRef = useRef(null);
  const [google, setGoogle] = useState(null);
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const clusterRef = useRef(null);
  const infoWindowRef = useRef(null);
  const fittedRef = useRef(false); // fit bounds only on the first marker render

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    apiReports.getOutOfArea(thresholdMinutes).then((res) => {
      if (cancelled) { return; }
      const r = res?.data?.result;
      if (res?.data?.status === 200 && r) { setResult(r); }
      else { setError(res?.data?.message || "Failed to load out-of-area inspections."); }
      setLoading(false);
    }).catch(() => { if (!cancelled) { setError("Failed to load out-of-area inspections."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [thresholdMinutes]);

  // init the map once — same mapId as OpenMap so the tag markers (content divs) work
  useEffect(() => {
    let cancelled = false;
    loadGoogle().then((g) => {
      if (cancelled) { return; }
      setGoogle(g);
      const m = new g.maps.Map(mapDivRef.current, {
        center: { lat: 28, lng: -82 },
        zoom: 5,
        mapTypeControl: false,
        streetViewControl: false,
        scaleControl: true,
        mapId: "8041c9b566fb264",
      });
      setMap(m);
      infoWindowRef.current = new g.maps.InfoWindow();
    }).catch(() => { if (!cancelled) { setError("Failed to load map."); } });
    return () => { cancelled = true; };
  }, []);

  // "How it's measured" info: work-area anchors are the drive-time origins.
  const farthestFirst = (a, b) => (b.driveTimeMinutes < 0 ? Infinity : b.driveTimeMinutes) - (a.driveTimeMinutes < 0 ? Infinity : a.driveTimeMinutes);
  const items = useMemo(() => [...result?.items || []].sort(farthestFirst), [result]);
  const spotSorted = useMemo(() => [...spotItems || []].sort(farthestFirst), [spotItems]);
  // In spot mode the table + map show the same flagged set, re-timed from the
  // picked point; otherwise the anchor-based classification.
  const displayItems = spotMode && spotItems ? spotSorted : items;

  // policyNo -> newest batch containing it (batches arrive newest-first).
  const batchByPolicy = useMemo(() => {
    const m = new Map();
    for (const b of batches) {
      for (const mem of b.members || []) {
        if (!m.has(mem.policyNo)) { m.set(mem.policyNo, b); }
      }
    }
    return m;
  }, [batches]);

  const toggleSelected = (policyNo) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(policyNo)) { next.delete(policyNo); } else { next.add(policyNo); }
      return next;
    });
  };

  // client-side search like the Penalty Risk tab
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) { return displayItems; }
    return displayItems.filter(i => [i.policyNo, i.address, i.city, i.state, i.county, i.nearestWorkArea]
      .some(v => (v || '').toLowerCase().includes(q)));
  }, [displayItems, search]);

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (k) => {
    if (sortKey === k) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(k); setSortDir('asc'); }
  };
  const sortValue = (item, k) => {
    if (k === 'driveTimeMinutes') { return item.driveTimeMinutes < 0 ? Infinity : item.driveTimeMinutes; }
    if (k === 'location') { return `${item.city || ''}, ${item.state || ''}`; }
    if (k === 'appointmentDate') { return item.appointmentDate ? new Date(item.appointmentDate).getTime() : 0; }
    return item[k];
  };
  const sorted = useMemo(() => {
    if (!sortKey) { return shown; }
    return [...shown].sort((a, b) => {
      const av = sortValue(a, sortKey), bv = sortValue(b, sortKey);
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [shown, sortKey, sortDir]);

  const columns = [
    ['Policy #', 'policyNo'],
    ['Address', 'address'],
    ['City, State', 'location'],
    ...spotMode ? [] : [['Nearest Area', 'nearestWorkArea']],
    [spotMode ? 'From Spot' : 'Drive Time', 'driveTimeMinutes'],
    ['Age', 'ageDays'],
    ['Type', 'policyStatus'],
    ['Appointment', 'appointmentDate'],
    ['Status', 'status'],
  ];
  // Selection changes restyle pins in place by DOM id - the markers effect must
  // NOT rebuild on selection (every pin flashed on each toggle).
  useEffect(() => {
    selectedRef.current = selected;
    shown.forEach((i) => {
      const tag = document.getElementById(`ooa-${i.policyNo}`);
      if (!tag) { return; }
      if (selected.has(i.policyNo)) {
        tag.style.outline = "3px solid #111827";
        tag.style.outlineOffset = "1px";
      } else {
        tag.style.outline = "";
        tag.style.outlineOffset = "";
      }
    });
  }, [selected, shown]);

  const createBatch = async () => {
    if (selected.size === 0) { return; }
    try {
      const res = await apiReports.createOutOfAreaBatch([...selected]);
      if (res?.status >= 400) {
        toast.error(res?.data?.message || "Could not create batch");
        return;
      }
      toast.success(`Batch #${res.data.result.id} created`);
      setSelected(new Set());
      loadBatches();
    } catch {
      toast.error("Error creating batch");
    }
  };

  const markReturned = async (id, dateStr) => {
    try {
      const res = await apiReports.returnOutOfAreaBatch(id, dateStr);
      if (res?.status >= 400) {
        toast.error(res?.data?.message || "Could not set the returned date");
        return;
      }
      toast.success("Batch marked returned");
      setEditingReturn(null);
      loadBatches();
    } catch {
      toast.error("Error marking batch returned");
    }
  };

  const startEditReturn = (batch) => {
    setEditingReturn(batch.id);
    setReturnDateDraft(batch.returned ? batch.returnedAt.slice(0, 10) : localDateStr(new Date()));
  };

  // Map clicks pick the spot (mode only).
  useEffect(() => {
    if (!map || !google || !spotMode) { return; }
    const listener = google.maps.event.addListener(map, "click", (e) => {
      if (e.latLng) { setSpotPoint(e.latLng.toJSON()); }
    });
    return () => { google.maps.event.removeListener(listener); };
  }, [map, google, spotMode]);

  // Fetch re-timed items whenever the spot moves.
  useEffect(() => {
    if (!spotMode || !spotPoint) { return; }
    let cancelled = false;
    apiReports.spotCheckOutOfArea(spotPoint.lat, spotPoint.lng, thresholdMinutes).then((res) => {
      if (cancelled) { return; }
      if (res?.data?.status === 200) { setSpotItems(res.data.result?.items || []); }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [spotMode, spotPoint, thresholdMinutes]);

  // render markers whenever data + map are ready
  useEffect(() => {
    if (!map || !google || loading) { return; } // frozen during reclassify: no color flash (green-then-gone)
    let cancelledLocal = false;
    google.maps.importLibrary("marker").then((markerLib) => {
      if (cancelledLocal) { return; }
      if (clusterRef.current) { clusterRef.current.clearMarkers(); clusterRef.current.setMap(null); clusterRef.current = null; }
      markersRef.current.forEach((m) => { m.setMap(null); });
      markersRef.current = [];
      const infoWindow = infoWindowRef.current;
      const bounds = new google.maps.LatLngBounds();
      const AdvancedMarkerElement = markerLib.AdvancedMarkerElement;
      const markers = [];

      // work-area anchors: small blue tags with area initials, click to identify
      // and list the inspectors rotated to the area this month.
      // The picked spot: purple tag, the origin of every displayed time.
      if (spotPoint) {
        const spotTag = document.createElement("div");
        spotTag.className = "inspection-map-tag inspection-map-tag-spot";
        const spotSpan = document.createElement("span");
        spotSpan.textContent = "\u25CE";
        spotTag.appendChild(spotSpan);
        const spotMarker = new AdvancedMarkerElement({
          map,
          position: spotPoint,
          content: spotTag,
          title: "Picked spot",
        });
        markers.push(spotMarker);
        bounds.extend(spotMarker.position);
      }

      (result?.workAreas || []).forEach((a) => {
        const tag = document.createElement("div");
        tag.className = "inspection-map-tag inspection-map-tag-anchor";
        tag.title = a.name;
        const span = document.createElement("span");
        span.textContent = areaInitials(a.name);
        tag.appendChild(span);
        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: a.lat, lng: a.lng },
          content: tag,
          title: `${a.name} (area anchor)`,
        });
        marker.addListener("click", () => {
          apiWorkAreas.getInspectors(a.workAreaId).then((res) => {
            if (cancelledLocal) { return; }
            const div = document.createElement("div");
            div.className = "tw-text-sm";
            const strong = document.createElement("strong");
            strong.textContent = a.name;
            div.appendChild(strong);
            div.appendChild(document.createElement("br"));
            div.appendChild(document.createTextNode(a.address ? `Anchor point: ${a.address}` : "Area anchor point"));
            div.appendChild(document.createElement("br"));
            div.appendChild(document.createElement("br"));
            const rotation = res?.data?.status === 200 && res?.data?.result || [];
            div.appendChild(document.createTextNode(rotation.length ? "Rotated inspectors:" : "No inspectors rotated."));
            div.appendChild(document.createElement("br"));
            rotation.slice(0, 20).forEach((it) => {
              div.appendChild(document.createTextNode(it.name));
              div.appendChild(document.createElement("br"));
            });
            if (rotation.length > 20) {
              div.appendChild(document.createTextNode(`… +${rotation.length - 20} more`));
            }
            infoWindow.setContent(div);
            infoWindow.open({ anchor: marker, map });
          }).catch(() => {});
        });
        markers.push(marker);
        bounds.extend(marker.position);
      });

      shown.forEach((i) => {
        if (!i.lat && !i.long) { return; }
        const batch = batchByPolicy.get(i.policyNo);
        if (batch && hiddenBatches.has(batch.id)) { return; } // batch toggled off the map
        // Rectangular severity tag showing the minutes, OpenMap style; batched
        // inspections go gray with their batch number.
        const markerTag = document.createElement("div");
        markerTag.className = batch
          ? "inspection-map-tag inspection-map-tag-batched"
          : spotMode
            ? `inspection-map-tag inspection-map-tag-${SEVERITY(i.driveTimeMinutes, thresholdMinutes)}`
            : `inspection-map-tag inspection-map-tag-${AGE_BAND(i.ageDays)}`;
        markerTag.style.fontSize = "0.75rem";
        markerTag.style.fontWeight = "700";
        markerTag.style.lineHeight = "2rem";
        markerTag.id = `ooa-${i.policyNo}`;
        const span = document.createElement("span");
        span.textContent = batch ? `#${batch.id}` : i.driveTimeMinutes < 0 ? "?" : String(Math.round(i.driveTimeMinutes));
        markerTag.appendChild(span);

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: i.lat, lng: i.long },
          content: markerTag,
          title: `${i.policyNo} — ${spotMode ? minutesLabel(i.driveTimeMinutes) : `${i.ageDays}d old`}`,
        });
        marker.addListener("click", () => {
          // OpenMap pattern: with a selection active, pin clicks toggle it;
          // otherwise open the popup (which carries a Select button).
          if (selectedRef.current.size > 0) {
            toggleSelected(i.policyNo);
            return;
          }
          // InfoWindow content is plain-DOM (no HTML interpolation of DB text).
          const div = document.createElement("div");
          div.className = "tw-text-sm";
          const link = document.createElement("a");
          link.href = `/inspections/${i.policyNo}/process/pre_r`;
          link.target = "_blank";
          link.textContent = i.policyNo;
          const strong = document.createElement("strong");
          strong.appendChild(link);
          div.appendChild(strong); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(i.address || "")); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(`${i.city || ""}, ${i.state || ""} — ${i.county || ""}`)); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(`Type: ${i.inspectionType || "—"}`)); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(`Policy Type: ${i.policyStatus}`)); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(`Age: ${i.ageDays}d`)); div.appendChild(document.createElement("br"));
          div.appendChild(document.createTextNode(`${hoursMinutes(i.driveTimeMinutes)} from ${i.nearestWorkArea}`));
          if (batch) {
            div.appendChild(document.createElement("br"));
            div.appendChild(document.createTextNode(batch.returned ? `In batch #${batch.id} (returned)` : `In batch #${batch.id}`));
          }
          const selectBtn = document.createElement("button");
          selectBtn.className = "tw-mt-2 tw-px-3 tw-py-1 tw-text-xs tw-text-white tw-bg-primary tw-rounded";
          selectBtn.textContent = "Select";
          selectBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            toggleSelected(i.policyNo);
            infoWindow.close();
          });
          div.appendChild(document.createElement("br"));
          div.appendChild(selectBtn);
          infoWindow.setContent(div);
          infoWindow.open({ anchor: marker, map });
        });
        markers.push(marker);
        bounds.extend(marker.position);
      });

      markersRef.current = markers;
      if (markers.length) {
        // Fit once on first render only — spot checks and mode toggles must not
        // yank the user's viewport back to full extents.
        if (!fittedRef.current) {
          map.fitBounds(bounds);
          fittedRef.current = true;
        }
        if (markers.length > 2000) { clusterRef.current = new MarkerClusterer({ map, markers }); }
      }
    }).catch(() => { if (!cancelledLocal) { setError("Failed to load markers."); } });
    return () => { cancelledLocal = true; };
  }, [map, google, result, shown, spotPoint, thresholdMinutes, batchByPolicy, hiddenBatches]);


  return (
    <div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-mb-2">
        <h3 className="tw-font-medium">Out of Area Inspections</h3>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search policy #, address, area…"
          className="tw-ml-auto tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary"
          style={{ minWidth: '240px' }}
        />
      </div>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-3" style={{ minHeight: '2.5rem' }}>
        {spotMode
          ? "Spot check: click anywhere on the map — every time below re-measures from that point (same flagged inspections), so you can judge whether the spot covers them."
          : `Beyond ${thresholdMinutes / 60} hours' drive from the nearest work-area anchor point. Small blue tags are the anchor points (set on the Work Areas page); tag colour shows how far past the limit each inspection is.`}
      </p>
      {error && <p className="tw-text-sm tw-text-red-600 tw-mb-4">{error}</p>}

      {/* Threshold slider */}
      <div className="tw-flex tw-items-start tw-gap-3 tw-mb-2">
        <label htmlFor="ooa-thresh" className="tw-text-sm tw-text-gray-600 tw-whitespace-nowrap tw-pt-1">Out of area after</label>
        <div>
          <input
            id="ooa-thresh"
            type="range"
            min="120"
            max="240"
            step="30"
            value={sliderValue}
            onChange={(e) => setSliderValue(Number(e.target.value))}
            onPointerUp={() => setThresholdMinutes(sliderValue)}
            onKeyUp={() => setThresholdMinutes(sliderValue)}
            className="tw-w-48"
            title="Drive-time limit for classifying inspections as out of area — releases reclassify"
          />
          <div className="tw-flex tw-justify-between tw-text-xs tw-w-48 tw-select-none tw-mt-0.5">
            {[120, 150, 180, 210, 240].map((v) =>
              <span key={v} className={v === sliderValue ? "tw-font-semibold tw-text-gray-700" : "tw-text-gray-400"}>{v / 60}h</span>
            )}
          </div>
        </div>
        <button
          onClick={() => { setSpotMode(!spotMode); setSpotItems(null); setSpotPoint(null); }}
          className={`tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-md ${spotMode ? "tw-bg-primary tw-text-white tw-border-primary" : "tw-border-gray-300 hover:tw-bg-gray-50"}`}
          title="Re-measure every displayed drive time from a point you pick on the map"
        >{spotMode ? "Exit spot check" : "Spot check"}</button>
        {loading && <span className="tw-text-sm tw-text-gray-500 tw-pt-1">Reclassifying…</span>}
        {/* Colour key: age bands normally, drive-time bands in spot check; batched gray always */}
        <div className="tw-ml-auto tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          {(spotMode
            ? [[ "#16a34a", `under ${thresholdMinutes / 60}h` ],
              [ "#fbbf24", `${thresholdMinutes / 60}–${thresholdMinutes / 60 + 1}h` ],
              [ "#f97316", `${thresholdMinutes / 60 + 1}–${thresholdMinutes / 60 + 3}h` ],
              [ "#7f1d1d", `beyond ${thresholdMinutes / 60 + 3}h` ]]
            : [[ "#16a34a", "0-7d" ],
              [ "#a3d977", "8-14d" ],
              [ "#fbbf24", "15-21d" ],
              [ "#f97316", "22-30d" ],
              [ "#ef4444", "31-45d" ],
              [ "#7f1d1d", "45+d" ]])
            .map(([c, l]) => 
              <span key={l} className="tw-flex tw-items-center tw-gap-1">
                <span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded" style={{ backgroundColor: c }} />
                <span className="tw-text-xs tw-text-gray-500">{l}</span>
              </span>
            )}
          <span className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-inline-block tw-w-3 tw-h-3 tw-rounded" style={{ backgroundColor: "#4b5563" }} />
            <span className="tw-text-xs tw-text-gray-500">batched</span>
          </span>
        </div>
      </div>

      {/* Map */}
      <div ref={mapDivRef} className="tw-w-full tw-bg-gray-100 tw-rounded-lg tw-mb-6" style={{ height: 690 }} />

      {/* Table */}
      <div data-tour="ooa-table">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-2">
          <p className="tw-text-sm tw-text-gray-600">
            {spotMode
              ? <>Showing <strong>{sorted.length}</strong> flagged inspections re-timed from your picked spot, farthest first.</>
              : <>Showing <strong>{sorted.length}</strong> of <strong>{items.length}</strong> out-of-area inspections, farthest first.</>}
          </p>
          <button
            onClick={createBatch}
            disabled={selected.size === 0}
            className={`tw-ml-auto tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-md ${selected.size === 0 ? "tw-border-gray-200 tw-text-gray-400 tw-cursor-not-allowed" : "tw-border-primary tw-text-primary hover:tw-bg-blue-50"}`}
            title="Group the selected inspections into a batch for return to Orchid"
          >Create batch{selected.size > 0 ? ` (${selected.size})` : ""}</button>
        </div>
        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
          <thead className="tw-bg-fs-dark-blue tw-bg-opacity-25">
            <tr>
              <th className="tw-px-4 tw-py-3">
                <input
                  type="checkbox"
                  checked={sorted.length > 0 && selected.size === sorted.length}
                  onChange={() => setSelected(selected.size === sorted.length ? new Set() : new Set(sorted.map((i) => i.policyNo)))}
                  title="Select all"
                />
              </th>
              {columns.map(([label, k]) => 
                <th key={k} className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-cursor-pointer tw-select-none hover:tw-text-primary" onClick={() => toggleSort(k)}>
                  {label} {sortKey === k ? sortDir === 'asc' ? '▲' : '▼' : ''}
                </th>
              )}
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-gray-200">
            {sorted.map((i, n) => 
              <tr key={i.policyNo} className={`${n % 2 ? "tw-bg-gray-50" : "tw-bg-white"} hover:tw-bg-blue-50`}>
                <td className="tw-px-4 tw-py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(i.policyNo)}
                    onChange={() => toggleSelected(i.policyNo)}
                    title="Select for batching"
                  />
                </td>
                <td className="tw-px-6 tw-py-2 tw-whitespace-nowrap">
                  <a className="tw-text-primary hover:tw-underline" target="_blank" rel="noreferrer" href={`/inspections/${i.policyNo}/process/pre_r`}>{i.policyNo}</a>
                </td>
                <td className="tw-px-6 tw-py-2">{i.address}</td>
                <td className="tw-px-6 tw-py-2 tw-whitespace-nowrap">{i.city}, {i.state}</td>
                {!spotMode && <td className="tw-px-6 tw-py-2">{i.nearestWorkArea}</td>}
                <td className="tw-px-6 tw-py-2 tw-whitespace-nowrap">{minutesLabel(i.driveTimeMinutes)}</td>
                <td className="tw-px-6 tw-py-2 tw-whitespace-nowrap">{i.ageDays}d</td>
                <td className="tw-px-6 tw-py-2">{i.policyStatus}</td>
                <td className="tw-px-6 tw-py-2 tw-whitespace-nowrap">{i.appointmentDate ? new Date(i.appointmentDate).toLocaleDateString() : "—"}</td>
                <td className="tw-px-6 tw-py-2">{i.prettyStatus || i.status}</td>
              </tr>
            )}
          </tbody>
        </table>
        {items.length === 0 && !loading && !error &&
          <p className="tw-text-sm tw-text-gray-500">No out-of-area inspections. Work areas need anchor points set on the Work Areas page before anything can be classified.</p>}
      </div>

      {/* Batches */}
      <div className="tw-mt-6">
        <h4 className="tw-font-medium tw-mb-2">Batches</h4>
        {batches.length === 0 &&
          <p className="tw-text-sm tw-text-gray-500">No batches yet — select inspections above and use Create batch.</p>}
        {batches.map((b) => 
          <div key={b.id} className="tw-py-2 tw-border-b tw-border-gray-200">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <button
                onClick={() => setExpandedBatch(expandedBatch === b.id ? null : b.id)}
                className="tw-text-sm tw-font-medium hover:tw-text-primary"
                title="Show this batch's inspections"
              >Batch #{b.id} {expandedBatch === b.id ? "▾" : "▸"}</button>
              <span className="tw-text-xs tw-text-gray-500">
                {b.createdAt}{b.createdBy ? ` by ${b.createdBy}` : ""} · {b.count} inspection{b.count === 1 ? "" : "s"}
              </span>
              {editingReturn === b.id
                ? <span className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="date"
                    value={returnDateDraft}
                    onChange={(e) => setReturnDateDraft(e.target.value)}
                    className="tw-border tw-border-gray-300 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs"
                    title="The date this batch was returned to Orchid"
                  />
                  <button onClick={() => markReturned(b.id, returnDateDraft)} className="tw-text-xs tw-text-primary hover:tw-underline">Save</button>
                  <button onClick={() => setEditingReturn(null)} className="tw-text-xs tw-text-gray-400 hover:tw-underline">Cancel</button>
                </span>
                : b.returned
                  ? <span className="tw-flex tw-items-center tw-gap-1">
                    <span className="tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-0.5 tw-rounded">Returned {b.returnedAt.slice(0, 10)}</span>
                    <button onClick={() => startEditReturn(b)} className="tw-text-xs tw-text-gray-400 hover:tw-underline" title="Change the returned date">edit</button>
                  </span>
                  : <button onClick={() => startEditReturn(b)} className="tw-text-xs tw-text-primary hover:tw-underline" title="Set the date this batch was returned to Orchid">Mark returned</button>}
              <button
                onClick={() => setHiddenBatches((prev) => { const next = new Set(prev); if (next.has(b.id)) { next.delete(b.id); } else { next.add(b.id); } return next; })}
                className="tw-ml-auto tw-text-xs tw-text-blue-600 hover:tw-underline"
              >{hiddenBatches.has(b.id) ? "Show on map" : "Hide from map"}</button>
            </div>
            {expandedBatch === b.id && 
            <table className="tw-min-w-full tw-mt-2 tw-text-xs">
              <thead className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                <tr>
                  {["Policy #", "Address", "City, State", "Age", "Type", "Appt", "Status"].map((h) =>
                    <th key={h} className="tw-px-3 tw-py-1.5 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-200">
                {(b.members || []).map((m) => 
                  <tr key={m.policyNo}>
                    <td className="tw-px-3 tw-py-1.5 tw-whitespace-nowrap">
                      <a className="tw-text-primary hover:tw-underline" target="_blank" rel="noreferrer" href={`/inspections/${m.policyNo}/process/pre_r`}>{m.policyNo}</a>
                    </td>
                    <td className="tw-px-3 tw-py-1.5">{m.address}</td>
                    <td className="tw-px-3 tw-py-1.5 tw-whitespace-nowrap">{m.city}, {m.state}</td>
                    <td className="tw-px-3 tw-py-1.5 tw-whitespace-nowrap">{m.ageDays}d</td>
                    <td className="tw-px-3 tw-py-1.5">{m.policyStatus}</td>
                    <td className="tw-px-3 tw-py-1.5 tw-whitespace-nowrap">{m.appointmentDate ? new Date(m.appointmentDate).toLocaleDateString() : "—"}</td>
                    <td className="tw-px-3 tw-py-1.5">{m.prettyStatus || m.status}</td>
                  </tr>
                )}
              </tbody>
            </table>
            }
          </div>
        )}
      </div>
    </div>
  );
}